import type { ReloadOptions } from "@inertiajs/core";
import { Head, Link, router, useForm, usePage, usePoll } from "@inertiajs/react";
import { type CellTemplateProp, type ColumnRegular, type HyperFunc, RevoGrid, Template, type VNode, defineCustomElements, h } from '@revolist/react-datagrid';
import SelectTypePlugin from '@revolist/revogrid-column-select';
import {
  AlertTriangle,
  ArrowLeft, Building2,
  Calendar,
  CheckCircle2,
  CheckCircle,
  ClipboardList,
  Clock,
  Copy,
  DollarSign,
  ExternalLink,
  FileText,
  Flag,
  Info,
  LoaderCircle,
  Mail,
  Package,
  Plus,
  Trash2,
  TriangleAlert,
  Upload
} from "lucide-react";
import { type ComponentProps, useEffect, useRef, useState } from "react";
import Dropzone from "shadcn-dropzone";


import PurchaseRequestApprovalPanel from "@/components/approvals/purchase-request-approval-panel";
import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import CommentsThread from "@/components/comments-thread";
import CurrencyFormatHint from "@/components/currency-format-hint";
import DeliveryNotesTable from "@/components/delivery-notes-table";
import GroupGate from "@/components/group-gate";
import InlineEditableCombobox from "@/components/inline-editable-combobox";
import InlineEditableField from "@/components/inline-editable-field";
import InvitationsManager from "@/components/invitations-manager";
import InvoicesTable from "@/components/invoices-table";
import PdfViewerSheet from "@/components/pdf-viewer-sheet";
import PriceComparisonTable from "@/components/price-comparison-table";
import PurchaseHistorySheet from "@/components/purchase-history-sheet";
import PurchaseRequestAssignmentsManager from "@/components/purchase-request-assignments-manager";
import PurchaseSelectionTable from "@/components/purchase-selection-table";
import HistoryDialog from "@/components/quote-history-dialog";
// Revogrid cells
import StatusBadge from "@/components/request-status/status-badge";
import StatusHistoryDialog from "@/components/request-status/status-history-dialog";
import StatusPicker from "@/components/request-status/status-picker";
import { RequestItemActionsCell } from "@/components/revogrid-cells/RequestItemActionsCell";
import SelectedItemsTable from "@/components/selected-items-table";
import TutorialBadge from "@/components/tutorial-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup, ButtonGroupSeparator, ButtonGroupText } from "@/components/ui/button-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import { GROUPS } from "@/constants/groups";
import { canExportPurchaseOrders, purchaseRequestStatusColor, purchaseRequestStatusLabel } from "@/constants/purchase-request";
import { formatCurrency } from '@/helpers/currency-formatter';
import { computeRevoGridHeight } from '@/helpers/revogrid';
import { useRevoGridEdit } from '@/hooks/use-revogrid-edit';
import AppLayout from "@/layouts/app-layout";
import { canSeeApprovalComponent } from "@/lib/authz";
import { cn } from "@/lib/utils";
import { purchaseRequestQuotePath, quoteDocumentPath } from "@/routes";
import {
  newQuoteRequestPurchaseRequestPath,
  purchaseRequestPath,
  purchaseRequestsPath,
  requestItemPath,
  requestItemsPath,
  uploadAndProcessQuotesPurchaseRequestPath,
} from "@/routes";
import type {
  BreadcrumbItem,
  Comment,
  DeliveryNote,
  LastPurchaseSummary,
  PriceComparison,
  PurchaseHistoryEntry,
  PurchaseRequest,
  PurchaseRequestApprovalState,
  PurchaseRequestAssignments,
  PurchaseRequestInvoiceSummary,
  Quote,
  QuoteDocument,
  QuoteInvitation,
  RequestItem,
  SharedData,
} from "@/types";
import { formatDateAR } from '@/utils/date';
import { formatFileSize, isPdfUrl } from "@/utils/file";

defineCustomElements();

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Solicitudes Compras",
    href: purchaseRequestsPath(),
  },
];

interface PurchaseRequestShowProps {
  default_supplier_id?: number;
  purchase_request: PurchaseRequest & {
    general_observations?: string;
    sector: {
      id: number;
      name: string;
      code?: string;
    };
  };
  can_edit_status?: boolean;
  status_history?: { at: string; by?: { id: number; name?: string } | null; from?: string | null; to?: string | null }[];
  comments: Comment[];
  quote_documents: QuoteDocument[];
  quotes: Quote[];
  request_items: RequestItem[];
  sectors?: { id: number; name: string }[];
  quote_invitations: QuoteInvitation[];
  price_comparison?: PriceComparison | null;
  // New: last purchase summary per request item
  last_purchases_by_request_item_id?: Record<number, LastPurchaseSummary>;
  // New (deferred): full history per request item (loaded on demand)
  purchase_history_by_request_item_id?: Record<number, PurchaseHistoryEntry[]>;
  assignments: PurchaseRequestAssignments;
  approval: PurchaseRequestApprovalState;
  delivery_notes?: DeliveryNote[];
  invoices?: PurchaseRequestInvoiceSummary[];
}

// Status colors and labels come from shared constants

const docStatusConfig = {
  pending: {
    label: "En espera",
    color: "bg-yellow-100 text-yellow-800",
    icon: "🟡",
  },
  processing: {
    label: "Procesando...",
    color: "bg-blue-100 text-blue-800",
    icon: "🔵",
  },
  processed: {
    label: "Procesado",
    color: "bg-green-100 text-green-800",
    icon: "✅",
  },
  failed: { label: "Error", color: "bg-red-100 text-red-800", icon: "❌" },
};

// Dates come already formatted from backend as DD/MM/YYYY



export default function PurchaseRequestShow({
  default_supplier_id,
  purchase_request,
  can_edit_status = false,
  status_history = [],
  comments,
  quote_documents,
  quotes,
  request_items,
  sectors = [],
  quote_invitations = [],
  price_comparison = null,
  last_purchases_by_request_item_id = {},
  assignments,
  approval,
  delivery_notes,
  invoices,
}: PurchaseRequestShowProps) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [qtyStr, setQtyStr] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<AsyncOption | null>(null);
  // Product picker for editing existing items (async)
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerItem, setProductPickerItem] = useState<RequestItem | null>(null);
  const [productPickerOption, setProductPickerOption] = useState<AsyncOption | null>(null);
  // Purchase history sheet state
  const [historySheetOpen, setHistorySheetOpen] = useState(false);
  const [historyItemId, setHistoryItemId] = useState<number | null>(null);
  const [historyItemDescription, setHistoryItemDescription] = useState<string | null>(null);
  const openHistory = (requestItemId: number, description: string) => {
    setHistoryItemId(requestItemId);
    setHistoryItemDescription(description);
    setHistorySheetOpen(true);
  };
  // Tabs manage view between comparativa and selección

  // Revogrid ref
  const gridRef = useRef<HTMLRevoGridElement>(null);

  // Group quotes by supplier and keep only the latest per supplier
  const groupsBySupplier = quotes.reduce((acc: Record<string, Quote[]>, q) => {
    const isDefault = default_supplier_id ? q.supplier.id === default_supplier_id : q.supplier.name === 'Proveedor por asignar';
    const key = isDefault ? `unknown-${q.id}` : `supplier-${q.supplier.id}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(q);
    return acc;
  }, {});

  const supplierQuoteGroups = Object.values(groupsBySupplier).map((arr) => {
    const sorted = arr.slice().sort((a, b) => {
      const ak = a.created_at ? new Date(a.created_at).getTime() : a.id;
      const bk = b.created_at ? new Date(b.created_at).getTime() : b.id;
      return bk - ak; // DESC
    });
    return { latest: sorted[0], history: sorted.slice(1) };
  });

  const latestQuotesOnly = supplierQuoteGroups.map((g) => g.latest);

  // Check default supplier only on latest per supplier
  const quotesWithDefaultSupplier = latestQuotesOnly.filter((quote) =>
    default_supplier_id ? quote.supplier.id === default_supplier_id : quote.supplier.name === 'Proveedor por asignar'
  );
  const hasDefaultSupplierQuotes = quotesWithDefaultSupplier.length > 0;

  // We no longer receive full product catalog or suppliers here.
  // Product selection is handled via async modal picker.

  // Units shared from backend via Inertia
  const shared = usePage<SharedData>().props;
  const userGroups = shared.auth?.groups ?? [];
  const canEditAssignments = userGroups.includes(GROUPS.buyer) || userGroups.includes(GROUPS.supervisor);
  const unitOptions = shared.enums?.units ?? [
    { label: 'UN - Unidades', value: 'UN' }
  ];

  // Determine if the origin document is a PDF or image for proper handling
  const originIsPdf = purchase_request?.pdf?.url ? isPdfUrl(purchase_request.pdf.url) : false;

  // Transform data for select: null/undefined → 0 for correct visualization
  const ROW_CLASS_PROP = '__rowClass';
  const UNMATCHED_ROW_CLASS = 'rg-row-unmatched';
  const transformedRequestItems = request_items.map(item => ({
    ...item,
    product_id: item.product_id ?? 0,
    [ROW_CLASS_PROP]: (item.product_id == null) ? UNMATCHED_ROW_CLASS : ''
  }));

  // Calculate grid height to avoid internal scroll
  const gridHeight = computeRevoGridHeight(transformedRequestItems.length);

  // Visual-only stats for header chips (no core logic touched)
  const totalItems = request_items.length;
  const assignedCount = request_items.filter((i: RequestItem) => i.product_id != null).length;
  const unassignedCount = Math.max(totalItems - assignedCount, 0);
  const pricedCount = request_items.filter((i: RequestItem) => Number(i.average_price ?? 0) > 0).length;
  const estimatedTotalARS = request_items.reduce((sum: number, i: RequestItem) => {
    const avg = Number(i.average_price ?? 0);
    const qty = Number(i.quantity ?? 0);
    return sum + avg * qty;
  }, 0);
  const hasUnmatchedItems = unassignedCount > 0;

  const openProductPicker = (item: RequestItem) => {
    setProductPickerItem(item);
    const current = item.product_id && item.product ? { value: item.product_id, label: item.product.normalized_name } : null;
    setProductPickerOption(current);
    setProductPickerOpen(true);
  };

  // Column definitions following RevoGrid patterns
  const hasRawDescriptions = request_items.some((i: RequestItem) => typeof i.raw_description === 'string' && i.raw_description.trim() !== "");
  const columns: ColumnRegular[] = [
    // Code field - editable text
    {
      prop: 'code',
      name: 'Código',
      editor: 'text',
      size: 150,
      minSize: 100,
    },
    // Description field - async product picker (double click)
    {
      prop: 'product_id',
      name: 'Descripción',
      size: 350,
      minSize: 250,
      readonly: true,
      // Display description/raw text; editing opens modal async picker on double click
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const model = cellProps.model as RequestItem;
        const displayText = model.description ?? model.raw_description ?? 'Sin producto asignado';
        return createElement('div', {
          className: 'flex items-center h-full px-2 text-sm cursor-pointer',
          ondblclick: () => openProductPicker(model),
          title: 'Doble clic para asignar producto',
        }, displayText);
      }
    },
    // Quantity field - numeric
    {
      prop: 'quantity',
      name: 'Cantidad',
      editor: 'number',
      size: 100,
      minSize: 80,
    },
    // Quantity received - read-only, shows received vs. requested
    {
      prop: 'quantity_received',
      name: 'Recibido',
      readonly: true,
      size: 120,
      minSize: 100,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const received = Number(cellProps.model.quantity_received ?? 0);
        const requested = Number(cellProps.model.quantity ?? 0);
        const isComplete = received >= requested && requested > 0;
        return createElement('div', {
          className: `text-right flex items-center h-full px-2 text-sm ${isComplete ? 'text-green-600 font-medium' : ''}`
        }, received.toString());
      }
    },
    // Unit field - select with common units
    {
      prop: 'unit',
      name: 'Unidad',
      columnType: 'select',
      source: unitOptions,
      labelKey: 'label',
      valueKey: 'value',
      size: 150,
      minSize: 120,
    },
    // need_date
    {
      prop: "need_date",
      name: "Fecha de necesidad",
      readonly: true,
      size: 150,
      minSize: 120,
    },
    // Average price - read-only formatted currency
    {
      prop: 'average_price',
      name: 'Precio Promedio',
      readonly: true,
      size: 150,
      minSize: 120,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const value = Number(cellProps.value ?? 0);
        const formatted = value > 0 ? formatCurrency(value, 'ARS') : 'No cotizado';
        return createElement('div', {
          className: 'text-right flex items-center h-full px-2 text-sm'
        }, formatted);
      }
    },
    // Last purchase summary (read-only)
    {
      prop: '__last_purchase',
      name: 'Última compra',
      readonly: true,
      size: 260,
      minSize: 220,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const model = cellProps.model as RequestItem;
        const summary = last_purchases_by_request_item_id[model.id];
        const hasProduct = model.product_id != null && model.product_id !== 0;
        let content: string;
        if (!hasProduct) {
          content = 'Asigna un producto para ver historial';
        } else if (!summary) {
          content = 'Sin compras previas';
        } else {
          const dateStr = summary.created_at ? formatDateAR(summary.created_at) : '';
          const arsStr = typeof summary.unit_price_ars === 'number' && summary.unit_price_ars > 0
            ? formatCurrency(summary.unit_price_ars, 'ARS')
            : formatCurrency(summary.unit_price, summary.currency || 'ARS');
          content = `Última: ${arsStr} — ${summary.supplier_name} — ${dateStr}`;
        }

        return createElement('div', { className: 'flex items-center h-full px-2 text-xs text-muted-foreground' }, [
          createElement('span', { key: 'txt' }, content),
          createElement('button', {
            key: 'btn',
            type: 'button',
            className: 'ml-2 text-blue-600 hover:underline',
            onClick: () => openHistory(model.id, model.description),
          }, 'Ver historial'),
        ]);
      },
    },
    // Actions column
    {
      prop: 'actions',
      name: '',
      readonly: true,
      cellTemplate: Template(RequestItemActionsCell),
      size: 92,
      pin: 'colPinEnd',
      minSize: 92,
      maxSize: 92,
    },
  ];

  if (hasRawDescriptions) {
    // Insert an optional read-only column showing the original/raw description
    columns.splice(2, 0, {
      prop: 'raw_description',
      name: 'Original',
      readonly: true,
      size: 300,
      minSize: 200,
      cellTemplate: (createElement: HyperFunc<VNode>, cellProps: CellTemplateProp) => {
        const value = String(cellProps.value ?? '').trim();
        return createElement('div', {
          className: 'flex items-center h-full px-2 text-sm text-muted-foreground'
        }, value === '' ? '—' : value);
      }
    } as ColumnRegular);
  }

  // Register column plugins
  const columnTypes = {
    select: new SelectTypePlugin()
  };

  // RevoGrid-scoped style for unmatched rows (injected into grid shadow DOM)
  const gridRegisterNodes: VNode[] = [
    h('style', null, `
      /* Highlight entire unmatched row */
      .rgRow.${UNMATCHED_ROW_CLASS} { background-color: #FEF3C7; }
      /* Ensure cells inherit the highlight even with individual backgrounds */
      .rgRow.${UNMATCHED_ROW_CLASS} .rgCell { background-color: #FEF3C7; }
    `)
  ];

  const { data, setData, post, processing } = useForm({
    pdfs: [] as File[],
  });

  // Form for adding new RequestItem
  const { data: newItemData, setData: setNewItemData, post: postNewItem, processing: creatingItem, reset: resetNewItem } = useForm({
    request_item: {
      purchase_request_id: purchase_request.id,
      product_id: null as number | null,
      quantity: 1 as number,
      unit: '' as string,
    }
  });

  const parseCommaNumber = (raw: string): number | null => {
    if (raw.trim() === "") return null;
    if (!/^[0-9]+(,[0-9]+)?$/.test(raw)) return null;
    const parsed = Number(raw.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Metrics for "Cotizaciones Generadas" (visual only)
  const docCounts = quote_documents.reduce(
    (acc: Record<string, number>, d) => {
      acc[d.status] = (acc[d.status] || 0) + 1;
      return acc;
    },
    { pending: 0, processing: 0, processed: 0, failed: 0 } as Record<string, number>,
  );
  // Previously computed metrics not used in UI, removed to satisfy lint rules.

  // Handle RevoGrid cell editing with reusable hook
  useRevoGridEdit<RequestItem>({
    gridRef,
    pathGenerator: (item) => requestItemPath(item),
    resourceKey: 'request_item',
    valueProcessor: (prop, newValue) => {
      // Handle product_id field: 0 = null (unassigned), number = product ID
      if (prop === 'product_id') {
        return (newValue === 0 || newValue === '0' || newValue === undefined) 
          ? null 
          : Number(newValue);
      }
      
      // Numeric fields: parse as numbers
      const numericFields = ['quantity'];
      if (numericFields.includes(String(prop))) {
        return Number(newValue) || 0;
      }
      
      return newValue as string | number | null;
    }
  });

  // Poll every 5 seconds for processing updates, but pause while history sheet is open.
  type PollReloadOptions = ReloadOptions & { preserveState?: boolean };
  const pollReloadOptions: PollReloadOptions = {
    // Limit reload to props that reflect processing changes
    only: ["quote_documents", "quotes", "price_comparison", "approval"],
    preserveState: true,
  };

  const { start: startPoll, stop: stopPoll } = usePoll(
    5000,
    pollReloadOptions,
  );

  useEffect(() => {
    if (historySheetOpen) {
      stopPoll();
    } else {
      startPoll();
    }
  }, [historySheetOpen, startPoll, stopPoll]);

  const handleFileDrop = (acceptedFiles: File[]) => {
    setData("pdfs", [...data.pdfs, ...acceptedFiles]);
  };

  const handleFileRemove = (index: number) => {
    const newFiles = data.pdfs.filter((_, i) => i !== index);
    setData("pdfs", newFiles);
  };

  const handleUploadSubmit = () => {
    post(uploadAndProcessQuotesPurchaseRequestPath(purchase_request), {
      forceFormData: true,
      onSuccess: () => {
        setUploadDialogOpen(false);
        setData("pdfs", []);
      },
    });
  };

  const handleDeleteDocument = (docId: number) => {
    if (
      window.confirm(
        "¿Estás seguro? Eliminar este documento removerá permanentemente el archivo y cualquier cotización generada. Esta acción no se puede deshacer.",
      )
    ) {
      router.delete(quoteDocumentPath(docId));
    }
  };

  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    ...breadcrumbs,
    {
      title: `Solicitud #${purchase_request.request_number}`,
      href: "#",
    },
  ];

  // Role presets for readability and future reuse
  const CAN_MANAGE_QUOTES = [GROUPS.buyer, GROUPS.supervisor];

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head title={`Solicitud #${purchase_request.request_number}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link href={purchaseRequestsPath()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Solicitudes de compra
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              Solicitud #{purchase_request.request_number}
            </h1>
            {purchase_request?.trace_id && (
              <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="opacity-70">Trace ID:</span>
                <code className="rounded bg-muted px-1 py-0.5">
                  {purchase_request.trace_id}
                </code>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => navigator.clipboard?.writeText?.(purchase_request.trace_id ?? "")}
                    >
                      <Copy className="h-3 w-3" />
                      Copiar
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copiar Trace ID</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
            <ButtonGroup className="flex-wrap">
              <TutorialBadge asButton />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled
                className={`disabled:opacity-100 ${purchaseRequestStatusColor(purchase_request.status)}`}
                title={purchaseRequestStatusLabel(purchase_request.status)}
              >
                {purchaseRequestStatusLabel(purchase_request.status)}
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link href={`${purchaseRequestPath(purchase_request)}/activity_log`}>
                  <ClipboardList className="h-4 w-4 mr-1" /> Ver historial
                </Link>
              </Button>
              {can_edit_status && (
                <Button size="sm" variant="outline" onClick={() => setStatusDialogOpen(true)}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Cambiar estado
                </Button>
              )}
            </ButtonGroup>
            <StatusPicker
              open={statusDialogOpen}
              onOpenChange={setStatusDialogOpen}
              value={purchase_request.status}
              onSubmit={(newStatus) => {
                if (!newStatus || newStatus === purchase_request.status) return;
                router.patch(purchaseRequestPath(purchase_request), { purchase_request: { status: newStatus } }, { preserveScroll: true });
              }}
            />
            {purchase_request.priority && (
              <Badge variant="destructive">
                <Flag className="h-3 w-3 mr-1" />
                Prioritario
              </Badge>
            )}
            {/* Botón de eliminar movido al final de la página */}
          </div>
        </div>

        {/* General Information Card */}
        <Card>
          <CardHeader className="bg-gradient-to-br from-muted/40 to-transparent rounded-t-xl">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Información General
            </CardTitle>
            <CardDescription>
              Edita en línea con el ícono de lápiz. Enter guarda, Esc cancela.
            </CardDescription>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {purchase_request.deadline_at && (
                <Badge variant="outline" className="px-2 py-0.5">
                  <Calendar className="h-3 w-3 mr-1" />
                  {purchase_request.deadline_at}
                </Badge>
              )}
              {/* Ver fuente: texto crudo (cuando no hay documento origen) */}
              {purchase_request?.raw_input_text && !purchase_request?.pdf?.url ? (
                <Dialog open={sourceDialogOpen} onOpenChange={setSourceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="xs" variant="outline" className="h-6">
                      <FileText className="h-3 w-3 mr-1" /> Ver fuente (texto)
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Texto original de la solicitud</DialogTitle>
                      <DialogDescription>
                        Este es el texto que se usó para generar la solicitud de compra.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-3">
                      <pre className="whitespace-pre-wrap text-sm">
                        {purchase_request.raw_input_text}
                      </pre>
                    </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
    <div className="mt-3">
      <PurchaseRequestAssignmentsManager
        purchaseRequest={purchase_request}
        assignments={assignments}
        canEdit={canEditAssignments}
        leadingAction={
          purchase_request?.pdf?.url ? (
            originIsPdf ? (
              <PdfViewerSheet
                pdfUrl={purchase_request.pdf.url}
                title={`Solicitud #${purchase_request.request_number}`}
              >
                <Button size="sm" variant="outline" className="gap-2">
                  <FileText className="h-4 w-4" /> Ver documento origen
                </Button>
              </PdfViewerSheet>
            ) : (
              <Button size="sm" variant="outline" asChild className="gap-2">
                <a
                  href={purchase_request.pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FileText className="h-4 w-4" /> Ver documento origen
                </a>
              </Button>
            )
          ) : null
        }
      />
    </div>
  </CardHeader>
          <CardContent>
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <InlineEditableField
                label="Número de Solicitud"
                value={purchase_request.request_number}
                field="request_number"
                updateUrl={purchaseRequestPath(purchase_request)}
                icon={FileText}
                disabled={true}
              />

              <InlineEditableField
                label="Fecha de Solicitud"
                value={purchase_request.request_date}
                field="request_date"
                type="date"
                updateUrl={purchaseRequestPath(purchase_request)}
                icon={Calendar}
                formatValue={(value) =>
                  value ? String(value) : "No especificada"
                }
              />

              <InlineEditableField
                label="Fecha límite"
                value={purchase_request.deadline_at}
                field="deadline_at"
                type="datetime"
                updateUrl={purchaseRequestPath(purchase_request)}
                icon={Calendar}
                formatValue={(value) => value ? String(value) : "Sin fecha límite"}
                placeholder="DD/MM/AAAA HH:mm"
              />

              <InlineEditableCombobox
                label="Sector (Obra)"
                value={purchase_request.sector?.id}
                field="sector_id"
                updateUrl={purchaseRequestPath(purchase_request)}
                icon={Building2}
                options={sectors.map((sector) => ({
                  label: sector.name,
                  value: sector.id,
                }))}
                formatValue={() =>
                  purchase_request.sector?.name || "No asignado"
                }
                searchPlaceholder="Buscar sector..."
                emptyMessage="No se encontró ningún sector"
              />

              <InlineEditableField
                label="Número de sector"
                value={purchase_request.sector?.code}
                field="sector_code"
                icon={Building2}
                disabled={true}
                formatValue={(value) => value ? String(value) : "Sin código"}
              />

              <InlineEditableField
                label="Costo Estimado"
                value={purchase_request.estimated_cost}
                field="estimated_cost"
                type="number"
                updateUrl={purchaseRequestPath(purchase_request)}
                icon={DollarSign}
                formatValue={(value) =>
                  value ? formatCurrency(Number(value), "ARS") : "No especificado"
                }
                placeholder="0.00"
              />

              <InlineEditableField
                label="Prioritario"
                value={purchase_request.priority}
                field="priority"
                type="boolean"
                updateUrl={purchaseRequestPath(purchase_request)}
                icon={Flag}
              />

              {/* Estado actual + historial */}
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span>Estado actual:</span>
                <button
                  type="button"
                  onClick={() => setHistoryDialogOpen(true)}
                  className="inline-flex items-center"
                  title="Ver historial de estado"
                >
                  <StatusBadge status={purchase_request.status} />
                </button>
                {status_history && status_history.length > 0 && (
                  <span className="opacity-80">
                    Último cambio: {new Date(status_history[status_history.length - 1].at).toLocaleString()}
                  </span>
                )}
                <span className="opacity-60">·</span>
                <button
                  type="button"
                  onClick={() => setHistoryDialogOpen(true)}
                  className="underline text-primary hover:opacity-80"
                >
                  Ver historial de cambios
                </button>
              </div>
              <StatusHistoryDialog
                open={historyDialogOpen}
                onOpenChange={setHistoryDialogOpen}
                currentStatus={purchase_request.status}
                history={status_history}
              />
              </div>
            </div>

            <Separator className="my-6" />

            <div className="rounded-lg border bg-background p-3">
              <InlineEditableField
                label="Observaciones Generales"
                value={purchase_request.general_observations}
                field="general_observations"
                type="textarea"
                updateUrl={purchaseRequestPath(purchase_request)}
                icon={FileText}
                placeholder="Agregar observaciones..."
                maxLength={500}
              />
            </div>
          </CardContent>
        </Card>

        {/* Comentarios */}
        <CommentsThread
          comments={comments}
          commentsPath={`/purchase_requests/${purchase_request.id}/comments`}
          resourceLabel="la solicitud"
        />

        {/* Request Items */}
        <Card className="h-full min-h-[400px]">
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle>Ítems de la Solicitud de compra</CardTitle>
              <CardDescription>
                Haz clic en cualquier campo para editarlo. Enter guarda, Esc cancela.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <ButtonGroup>
                <Button asChild variant="outline" size="sm" className="flex items-center gap-2" title="Exportar ítems como PDF">
                  <a href={`${purchaseRequestPath(purchase_request)}/export_request_items.pdf`} target="_blank" rel="noopener noreferrer">
                    <FileText className="h-4 w-4" />
                    Exportar PDF
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddItemDialogOpen(true)}
                  className="flex items-center gap-2"
                  title="Agregar un producto a la solicitud"
                >
                  <Plus className="h-4 w-4" />
                  Agregar producto
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </CardHeader>
          <CardContent>
            {/* Toolbar chips */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <ButtonGroup className="pointer-events-none [&>*]:cursor-default [&>*]:hover:bg-transparent">
                <ButtonGroupText className="gap-1">
                  <Package className="h-3.5 w-3.5" /> {totalItems} ítems
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText className="gap-1">
                  <CheckCircle className="h-3.5 w-3.5" /> {assignedCount} con producto
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText className={cn(
                  "gap-1",
                  unassignedCount > 0 && "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                )}>
                  <AlertTriangle className="h-3.5 w-3.5" /> {unassignedCount} sin producto
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText className="gap-1">
                  <DollarSign className="h-3.5 w-3.5" /> {pricedCount} cotizados
                </ButtonGroupText>
                <ButtonGroupSeparator />
                <ButtonGroupText className="gap-1">
                  <DollarSign className="h-3.5 w-3.5" /> {formatCurrency(estimatedTotalARS, 'ARS')}
                </ButtonGroupText>
              </ButtonGroup>
            </div>

            <div className="mb-4">
              <CurrencyFormatHint />
            </div>

            {request_items.length > 0 ? (
              <div className="min-h-[300px] rounded-lg border bg-background p-2">
                {hasUnmatchedItems && (
                  <div className="mb-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Hay ítems sin producto asignado. Asigna los productos para ver la comparativa. (Haz doble clic en la celda para editar)
                  </div>
                )}
                <RevoGrid
                  ref={gridRef}
                  source={transformedRequestItems}
                  columns={columns}
                  columnTypes={columnTypes}
                  theme="material"
                  hideAttribution
                  rowClass={ROW_CLASS_PROP}
                  registerVNode={gridRegisterNodes}
                  stretch={true}
                  resize={true}
                  autoSizeColumn={true}
                  // Force height to fit all rows (no internal scroll)
                  style={{ height: `${gridHeight}px` }}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="mb-2">No hay ítems en esta Solicitud de compra</p>
                <p className="text-sm">
                  Los ítems se crean automáticamente al procesar un documento de solicitud
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal: Seleccionar producto (edición de ítem) */}
        <Dialog open={productPickerOpen} onOpenChange={(open) => {
          if (!open) {
            setProductPickerItem(null);
            setProductPickerOption(null);
          }
          setProductPickerOpen(open);
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Asignar producto</DialogTitle>
              <DialogDescription>
                Busca y selecciona un producto del catálogo para el ítem.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Producto</Label>
                <AsyncCombobox
                  value={productPickerOption}
                  onChange={(opt) => {
                    setProductPickerOption(opt);
                  }}
                  defaultOptions={true}
                  cacheOptions={true}
                  loadOptions={async (inputValue: string) => {
                    const params = new URLSearchParams();
                    params.set('term', inputValue || '');
                    const res = await fetch(`/products/search?${params.toString()}`);
                    if (!res.ok) return [];
                    const data = (await res.json()) as { value: number | string; label: string }[];
                    return data.map((d) => ({ value: d.value, label: d.label }));
                  }}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setProductPickerOpen(false);
                    setProductPickerItem(null);
                    setProductPickerOption(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    if (!productPickerItem) return;
                    const productId = productPickerOption?.value ? Number(productPickerOption.value) : null;
                    router.patch(requestItemPath(productPickerItem), {
                      request_item: { product_id: productId },
                    }, {
                      preserveScroll: true,
                      onSuccess: () => {
                        setProductPickerOpen(false);
                        setProductPickerItem(null);
                        setProductPickerOption(null);
                      }
                    });
                  }}
                  disabled={!productPickerItem}
                >
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal: Agregar producto a la solicitud */}
        <Dialog open={addItemDialogOpen} onOpenChange={(open) => {
          if (!open) {
            resetNewItem();
            setQtyStr("");
          }
          setAddItemDialogOpen(open);
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Agregar producto</DialogTitle>
              <DialogDescription>
                Selecciona un producto de la base, cantidad y unidad.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Producto (async combobox) */}
              <div className="space-y-2">
                <Label>Producto *</Label>
                <AsyncCombobox
                  value={selectedProduct}
                  onChange={(opt) => {
                    setSelectedProduct(opt);
                    setNewItemData('request_item', {
                      ...newItemData.request_item,
                      product_id: opt?.value ? Number(opt.value) : null,
                    });
                  }}
                  defaultOptions={true}
                  cacheOptions={true}
                  loadOptions={async (inputValue: string) => {
                    const params = new URLSearchParams();
                    params.set('term', inputValue || '');
                    const res = await fetch(`/products/search?${params.toString()}`);
                    if (!res.ok) return [];
                    // Expect: [{ id, value, label, internal_code }]
                    const data = (await res.json()) as { value: number | string; label: string }[];
                    return data.map((d) => ({ value: d.value, label: d.label }));
                  }}
                />
              </div>

              {/* Cantidad y Unidad */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cantidad *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Ej: 10 o 10,5"
                    value={qtyStr}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                        setQtyStr(raw);
                        const parsed = parseCommaNumber(raw);
                        setNewItemData('request_item', {
                          ...newItemData.request_item,
                          quantity: parsed ?? newItemData.request_item.quantity,
                        });
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidad *</Label>
                  <Select
                    onValueChange={(val) => setNewItemData('request_item', { ...newItemData.request_item, unit: val })}
                    value={newItemData.request_item.unit}
                  >
                    <SelectTrigger className="w-full h-10">
                      <SelectValue placeholder="Selecciona unidad" />
                    </SelectTrigger>
                    <SelectContent className="w-[--radix-select-trigger-width]">
                      {unitOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setAddItemDialogOpen(false);
                    resetNewItem();
                    setQtyStr("");
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    postNewItem(requestItemsPath(), {
                      preserveScroll: true,
                      onSuccess: () => {
                        resetNewItem();
                        setQtyStr("");
                        setAddItemDialogOpen(false);
                      }
                    })
                  }}
                  disabled={
                    creatingItem ||
                    !newItemData.request_item.product_id ||
                    !newItemData.request_item.unit ||
                    qtyStr.trim() === '' || parseCommaNumber(qtyStr) === null
                  }
                >
                  {creatingItem ? 'Guardando…' : 'Agregar Ítem'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Gestión de Invitaciones — buyer/supervisor (oculto en compra directa) */}
        {!purchase_request.direct_purchase && (
          <GroupGate anyOf={[...CAN_MANAGE_QUOTES]}>
            <InvitationsManager
              purchaseRequestId={purchase_request.id}
              purchaseRequestNumber={purchase_request.request_number}
              existingInvitations={quote_invitations}
            />
          </GroupGate>
        )}

        {/* Envío de Solicitudes y Carga de Documentos — buyer/supervisor (oculto en compra directa) */}
        {!purchase_request.direct_purchase && (
          <GroupGate anyOf={[...CAN_MANAGE_QUOTES]}>
            <div className="grid w-full gap-6 md:grid-cols-2 items-stretch">
              {/* Quote Request Section */}
              <Card className="w-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Envío de Solicitudes de Cotización
                  </CardTitle>
                  <CardDescription>
                    Envía solicitudes de cotización a proveedores
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild>
                    <Link
                      href={newQuoteRequestPurchaseRequestPath(purchase_request)}
                    >
                      Enviar Solicitud de Cotización
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Document Upload Section */}
              <Card className="w-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Carga de documentos de cotizaciones
                  </CardTitle>
                  <CardDescription>
                    Sube documentos (PDF o imágenes) para procesamiento automático
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Dialog
                    open={uploadDialogOpen}
                    onOpenChange={setUploadDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button>
                        <Upload className="h-4 w-4 mr-2" />
                        Subir documentos
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Subir documentos de cotizaciones</DialogTitle>
                        <DialogDescription>
                          Arrastra documentos (PDF o imágenes) aquí o haz clic para seleccionar
                          múltiples archivos de cotizaciones
                        </DialogDescription>
                      </DialogHeader>
                  <div className="space-y-4">
                    <div className="w-full">
                      <Dropzone
                        onDrop={handleFileDrop}
                        accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                        disabled={processing}
                        containerClassName="w-full"
                        dropZoneClassName="group relative w-full h-40 rounded-xl border border-solid border-border outline-none bg-gradient-to-br from-muted/40 to-muted/10 hover:from-accent/50 hover:to-accent/20 overflow-hidden"
                      >
                        {(dz) => (
                          <div className="relative flex h-full w-full items-center justify-center">
                            <div className="pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(600px_circle_at_center,black,transparent)] bg-[linear-gradient(to_right,rgba(120,119,198,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(120,119,198,0.08)_1px,transparent_1px)] bg-[size:14px_14px]" />
                            <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(400px_circle_at_center,rgba(99,102,241,0.14),transparent_60%)]" />
                            <div className="relative flex flex-col items-center text-center gap-2 px-6">
                              <div
                                className={`h-10 w-10 rounded-full ring-1 flex items-center justify-center transition-all ${
                                  dz.isDragAccept
                                    ? "bg-indigo-500/10 ring-indigo-400/50"
                                    : dz.isDragReject
                                    ? "bg-destructive/10 ring-destructive/40"
                                    : "bg-background/60 ring-border"
                                }`}
                              >
                                <Upload
                                  className={`h-5 w-5 transition-colors ${
                                    dz.isDragAccept
                                      ? "text-indigo-500"
                                      : dz.isDragReject
                                      ? "text-destructive"
                                      : "text-muted-foreground"
                                  }`}
                                />
                              </div>
                              <div className="text-sm font-medium">
                                {dz.isDragActive
                                  ? "Suelta los documentos para cargar"
                                  : data.pdfs.length > 0
                                  ? "Agregar más documentos"
                                  : "Arrastra documentos aquí o haz clic para cargar"}
                              </div>
                              <p className="text-[11px] text-muted-foreground/80">PDF o imágenes (JPG, PNG)</p>
                            </div>
                          </div>
                        )}
                      </Dropzone>
                    </div>

                    {data.pdfs.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium">
                          Archivos seleccionados ({data.pdfs.length}):
                        </p>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {data.pdfs.map((file, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center flex-row w-full h-12 px-3 border border-gray-200 rounded-lg"
                            >
                              <div className="flex items-center flex-row gap-3 h-full">
                                <FileText className="text-red-600 w-5 h-5" />
                                <div className="flex flex-col gap-0">
                                  <div className="text-sm font-medium leading-snug">
                                    {file.name}
                                  </div>
                                  <div className="text-xs text-gray-500 leading-tight">
                                    Documento • {formatFileSize(file.size)}
                                  </div>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFileRemove(index)}
                                disabled={processing}
                                className="p-1 h-8 w-8 rounded-full hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setUploadDialogOpen(false)}
                        disabled={processing}
                      >
                        Cancelar
                      </Button>
                      <Button
                        disabled={processing || data.pdfs.length === 0}
                        onClick={handleUploadSubmit}
                      >
                        {processing && (
                          <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                        )}
                        {processing
                          ? "Procesando..."
                          : `Subir y procesar ${data.pdfs.length} documento${data.pdfs.length !== 1 ? "s" : ""}`}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
          </GroupGate>
        )}

        {/* Cotizaciones generadas por IA — buyer/supervisor (oculto en compra directa) */}
        {!purchase_request.direct_purchase && (
          <GroupGate anyOf={[...CAN_MANAGE_QUOTES]}>
        {(quotes.length > 0 || quote_documents.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Cotizaciones generadas por IA
              </CardTitle>
              <CardDescription>
                Resultados procesados automáticamente desde los documentos
              </CardDescription>
              {/* Header metrics (compact) */}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="gap-1">
                      <FileText className="h-3.5 w-3.5" /> Docs: {quote_documents.length}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Documentos subidos</TooltipContent>
                </Tooltip>
                {docCounts.failed > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" /> Errores: {docCounts.failed}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>Archivos con error de procesamiento</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="gap-1">
                      <Mail className="h-3.5 w-3.5" /> Cotizaciones: {quotes.length}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Total de cotizaciones generadas</TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Documentos cargados - sección compacta */}
                {quote_documents.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm font-medium text-muted-foreground">
                      Documentos:
                    </span>
                    {quote_documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-2 px-2 py-1 bg-background rounded border text-xs"
                      >
                        <FileText className="h-3 w-3" />
                        <span className="max-w-24 truncate">
                          {doc.pdf?.filename ?? "Sin nombre"}
                        </span>
                        {(() => {
                          const status = doc.status as keyof typeof docStatusConfig;
                          const color = docStatusConfig[status]?.color;
                          const label = docStatusConfig[status]?.label;
                          const Icon = status === 'pending'
                            ? (props: ComponentProps<typeof Clock>) => <Clock {...props} />
                            : status === 'processing'
                            ? (props: ComponentProps<typeof Clock>) => (
                                <LoaderCircle
                                  {...props}
                                  className={(props.className ?? '') + ' animate-spin'}
                                />
                              )
                            : status === 'processed'
                            ? (props: ComponentProps<typeof Clock>) => <CheckCircle2 {...props} />
                            : (props: ComponentProps<typeof Clock>) => <TriangleAlert {...props} />;
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className={`text-xs px-1 py-0 h-5 gap-1 cursor-help ${color}`}>
                                  <Icon className="h-3 w-3" /> {label}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                Estado del documento
                              </TooltipContent>
                            </Tooltip>
                          );
                        })()}
                        {doc.pdf?.url && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-4 w-4 p-0"
                                asChild
                              >
                                <a
                                  href={doc.pdf.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Ver documento</TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 p-0 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Eliminar documento</TooltipContent>
                        </Tooltip>
                      </div>
                    ))}
                    {/* Ayuda discreta bajo la lista de documentos */}
                    <div className="w-full text-[11px] text-muted-foreground mt-1">
                      ¿La IA procesó mal el documento o hubo un error?
                      Elimina el archivo y súbelo nuevamente.
                      No olvides descargarlo antes de eliminarlo; de lo contrario, no podrás volver a subirlo.
                    </div>
                  </div>
                )}

                {/* Cotizaciones - elemento principal (última por proveedor) */}
                {supplierQuoteGroups.length > 0 ? (
                  <div className="space-y-2">
                    {/* Header row for alignment */}
                    <div className="hidden md:grid grid-cols-12 text-xs text-muted-foreground px-2 py-1">
                      <div className="col-span-6">Proveedor</div>
                      <div className="col-span-3">Indicadores</div>
                      <div className="col-span-3 text-right">Total</div>
                    </div>
                    {supplierQuoteGroups.map(({ latest: quote, history }) => (
                      <div key={quote.id} className="grid grid-cols-12 items-center gap-3 p-4 border rounded-xl bg-card/50 hover:bg-card transition-colors">
                        {/* Col A: proveedor + meta */}
                        <div className="col-span-12 md:col-span-6 min-w-0">
                          <div className="flex items-center gap-2 truncate">
                            <span className="font-medium truncate" title={quote.supplier.name}>
                              {quote.supplier.name}
                            </span>
                            {quote.tax_id_extracted && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded border text-muted-foreground bg-muted/50">
                                CUIT: {quote.tax_id_extracted}
                              </span>
                            )}
                            <span className="text-[10px] px-1.5 py-0.5 rounded border text-muted-foreground">
                              {quote.currency}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            #{quote.quote_number ?? quote.id} • {quote.quote_date}
                          </div>
                        </div>

                        {/* Col B: indicadores compactos */}
                        <div className="col-span-12 md:col-span-3">
                          <div className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs rounded-md bg-muted/30 px-2 py-1">
                            {quote.need_review && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1 text-destructive cursor-help">
                                    <AlertTriangle className="h-3 w-3" /> Revisión
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>La IA detectó posibles inconsistencias</TooltipContent>
                              </Tooltip>
                            )}
                            {(quote.missing_items_count ?? 0) > 0 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-300 cursor-help">
                                    <AlertTriangle className="h-3 w-3" /> Faltan {quote.missing_items_count} {quote.missing_items_count === 1 ? 'ítem' : 'ítems'}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>Ítems de la solicitud de compra sin cotización</TooltipContent>
                              </Tooltip>
                            )}
                            {quote.context_notes && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 cursor-help">
                                    <Info className="h-3 w-3" /> Condiciones
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>Incluye aclaraciones o condiciones especiales</TooltipContent>
                              </Tooltip>
                            )}
                            {!quote.need_review && !(quote.missing_items_count && quote.missing_items_count > 0) && !quote.context_notes && (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        </div>

                        {/* Col C: total + acciones */}
                        <div className="col-span-12 md:col-span-3 text-right">
                          <div className="text-xl font-semibold leading-none">
                            {formatCurrency(quote.total_amount, quote.currency)}
                          </div>
                          {quote.currency === "USD" && quote.total_in_ars && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {formatCurrency(quote.total_in_ars, "ARS")}
                            </div>
                          )}
                          <div className="mt-3 flex items-center justify-end gap-2">
                            <ButtonGroup>
                              <Button variant="outline" size="sm" asChild>
                                <Link href={purchaseRequestQuotePath(purchase_request, quote)}>
                                  Ver Cotización
                                </Link>
                              </Button>
                              {quote.pdf?.url && (
                                <PdfViewerSheet
                                  pdfUrl={quote.pdf.url}
                                  title={`Cotización ${quote.quote_number ?? `#${quote.id}`} - ${quote.supplier.name}`}
                                  triggerText="Ver documento"
                                />
                              )}
                            </ButtonGroup>
                            {history.length > 0 && (
                              <HistoryDialog
                                supplierName={quote.supplier.name}
                                purchaseRequestId={purchase_request.id}
                                versions={history}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  quote_documents.length > 0 && (
                    <div className="text-center py-6 text-muted-foreground">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">
                        Los documentos están siendo procesados...
                      </p>
                    </div>
                  )
                )}
              </div>
            </CardContent>
          </Card>
        )}
          </GroupGate>
        )}

        {/* Supplier Assignment Alert */}
        {hasDefaultSupplierQuotes && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Configuración de Proveedores Requerida
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Debes asignar un proveedor a todas las cotizaciones antes de
                    poder ver la comparativa de precios
                  </p>
                  <div className="mt-3 space-y-1">
                    {quotesWithDefaultSupplier.map((quote) => (
                      <Link
                        key={quote.id}
                        href={purchaseRequestQuotePath(purchase_request, quote)}
                        className="text-xs text-amber-700 hover:text-amber-900 underline block"
                      >
                        • Cotización #{quote.quote_number} - Asignar proveedor
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparativa y Selección — supervisor, buyer y participantes del flujo de aprobación */}
        {(() => {
          const userCanSeeComponent = canSeeApprovalComponent(
            userGroups,
            approval,
            shared.auth?.user?.id ?? 0,
            CAN_MANAGE_QUOTES
          );

          if (!userCanSeeComponent) return null;

          return price_comparison && !hasDefaultSupplierQuotes ? (
            <Card>
              <CardHeader>
                <CardTitle>Comparativa y selección</CardTitle>
                <CardDescription>
                  Revisa la comparativa y selecciona los productos a comprar por proveedor.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const hasSelections = Boolean(
                    approval?.has_purchase_selections ||
                    (price_comparison?.rows?.some((row) => row.prices?.some((p) => p?.is_selected)) ?? false)
                  );
                  const defaultTab = hasSelections ? "selection" : "comparative";
                  return (
                    <Tabs defaultValue={defaultTab}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <TabsList className="flex w-full flex-col gap-2 rounded-2xl bg-muted/60 p-1 sm:w-auto sm:flex-row sm:gap-0">
                          <TabsTrigger className="flex-1 sm:flex-none" value="comparative">Comparativa de precios</TabsTrigger>
                          <TabsTrigger className="flex-1 sm:flex-none" value="selection">Selección de compra</TabsTrigger>
                        </TabsList>
                      </div>
                      <TabsContent value="comparative" className="mt-4">
                        <PriceComparisonTable
                          comparison={price_comparison}
                          purchaseRequestId={purchase_request.id}
                          lastPurchasesByRequestItemId={last_purchases_by_request_item_id}
                          onOpenHistory={(id, desc) => openHistory(id, desc)}
                        />
                      </TabsContent>
                      <TabsContent value="selection" className="mt-4">
                        <div className="space-y-7">
                          <PurchaseSelectionTable
                            comparison={price_comparison}
                            purchaseRequestId={purchase_request.id}
                            canExport={canExportPurchaseOrders(approval.status)}
                            canSaveSelections={purchase_request.status !== 'approved'}
                            purchaseRequestInfo={{
                              id: purchase_request.id,
                              request_number: purchase_request.request_number,
                              sector: {
                                id: purchase_request.sector?.id,
                                name: purchase_request.sector?.name,
                                code: purchase_request.sector?.code ?? null,
                              },
                            }}
                            onSaved={() => router.reload({ only: ["price_comparison"] })}
                            lastPurchasesByRequestItemId={last_purchases_by_request_item_id}
                            onOpenHistory={(id, desc) => openHistory(id, desc)}
                          />
                          <SelectedItemsTable
                            comparison={price_comparison}
                            selectionsTotals={approval?.selections_totals}
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  );
                })()}
              </CardContent>
            </Card>
          ) : null;
        })()}



        <PurchaseRequestApprovalPanel
          approval={approval}
          purchaseRequestId={purchase_request.id}
          sectorId={purchase_request.sector?.id}
          sectorName={purchase_request.sector?.name}
        />

        {/* Facturas Relacionadas */}
        <InvoicesTable invoices={invoices} purchaseRequestId={purchase_request.id} />

        {/* Remitos Relacionados */}
        <DeliveryNotesTable deliveryNotes={delivery_notes} purchaseRequestId={purchase_request.id} />

        {/* Acciones destructivas al final */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Acciones</CardTitle>
              <CardDescription>Operaciones avanzadas sobre esta solicitud</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (window.confirm(
                      "¿Estás seguro de que quieres eliminar esta solicitud de compra?\n\n" +
                      "Esta acción eliminará PERMANENTEMENTE:\n" +
                      "• La solicitud de compra\n" +
                      "• Todos los items de la solicitud\n" +
                      "• Todas las cotizaciones recibidas\n" +
                      "• Todos los documentos\n" +
                      "• Todas las invitaciones enviadas\n\n" +
                      "Esta acción NO se puede deshacer."
                    )) {
                      router.delete(purchaseRequestPath(purchase_request), {
                        onSuccess: () => {
                          router.get(purchaseRequestsPath());
                        },
                      });
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar Solicitud de compra
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Historial de compras (right sheet) */}
        <PurchaseHistorySheet
          open={historySheetOpen}
          onOpenChange={setHistorySheetOpen}
          requestItemId={historyItemId ?? undefined}
          requestItemDescription={historyItemDescription ?? undefined}
        />
      </div>
    </AppLayout>
  );
}
